import { Component } from '@theme/component';

/** @extends {Component<{track: HTMLElement, position: HTMLElement}>} */
class AvReferenceCarousel extends Component {
  requiredRefs = ['track', 'position'];
  #controller;
  #observer;
  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }
  updatedCallback() {
    super.updatedCallback();
    this.#connect();
  }
  #connect() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    this.refs.track.addEventListener('scroll', () => this.#update(), { passive: true, signal: this.#controller.signal });
    this.addEventListener('shopify:block:select', event => {
      if (event.target instanceof HTMLElement) event.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
    }, { signal: this.#controller.signal });
    this.#observer = new ResizeObserver(() => this.#update());
    this.#observer.observe(this.refs.track);
    this.#update();
  }
  #update() {
    const track = this.refs.track;
    const width = track.clientWidth;
    if (!width) return;
    const max = Math.max(0, track.scrollWidth - width);
    const pages = Math.max(1, Math.ceil(max / width) + 1);
    const current = max ? Math.min(pages, Math.round(Math.abs(track.scrollLeft) / max * (pages - 1)) + 1) : 1;
    this.refs.position.textContent = `${current} / ${pages}`;
  }
  #move(direction) {
    const track = this.refs.track;
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: track.clientWidth * direction * (rtl ? -1 : 1), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  /** Show the preceding group of cards. */
  previous() { this.#move(-1); }
  /** Show the following group of cards. */
  next() { this.#move(1); }
  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
}

/** @extends {Component<{sticky?: HTMLElement, stickyPrice?: HTMLElement, stickyVariant?: HTMLSelectElement, stickyButton?: HTMLButtonElement}>} */
class AvReferenceProduct extends Component {
  #controller;
  #observer;
  #saleTimer;
  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }
  updatedCallback() {
    super.updatedCallback();
    this.#connect();
  }
  #connect() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    this.addEventListener('change', event => {
      if (event.target instanceof HTMLSelectElement && event.target.matches('[data-av-variant]')) this.#sync();
    }, { signal: this.#controller.signal });
    const button = this.querySelector('[data-av-add]');
    if (button && this.refs.sticky) {
      this.#observer = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (this.refs.sticky) this.refs.sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      });
      this.#observer.observe(button);
    }
    this.#sync();
    this.#scheduleSaleExpiry();
  }
  #scheduleSaleExpiry() {
    clearTimeout(this.#saleTimer);
    const promo = this.querySelector('[data-av-promo-deadline]');
    if (!(promo instanceof HTMLElement)) return;
    const remaining = Date.parse(promo.dataset.avPromoDeadline) - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) { promo.hidden = true; return; }
    this.#saleTimer = setTimeout(() => this.#scheduleSaleExpiry(), Math.min(remaining, 2147483647));
  }
  #sync() {
    const select = this.querySelector('[data-av-variant]');
    const button = this.querySelector('[data-av-add]');
    if (!(select instanceof HTMLSelectElement) || !(button instanceof HTMLButtonElement)) return;
    const option = select.selectedOptions[0];
    if (!option) return;
    if (this.refs.stickyVariant) this.refs.stickyVariant.value = select.value;
    if (this.refs.stickyPrice) this.refs.stickyPrice.textContent = option.dataset.price || '';
    if (this.refs.stickyButton) {
      this.refs.stickyButton.disabled = option.dataset.available !== 'true';
      this.refs.stickyButton.textContent = this.refs.stickyButton.disabled ? button.dataset.soldOut : button.dataset.label;
    }
  }
  /** @param {Event} event - Sticky variant selection. */
  selectVariant(event) {
    const select = this.querySelector('[data-av-variant]');
    if (!(event.target instanceof HTMLSelectElement) || !(select instanceof HTMLSelectElement)) return;
    select.value = event.target.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }
  /** Submit the existing product form, preserving cart-drawer and quantity behavior. */
  addToCart() {
    const button = this.querySelector('[data-av-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || !button.form) return;
    button.form.requestSubmit(button);
  }
  disconnectedCallback() {
    clearTimeout(this.#saleTimer);
    this.#controller?.abort();
    this.#observer?.disconnect();
    super.disconnectedCallback();
  }
}
if (!customElements.get('av-reference-carousel')) customElements.define('av-reference-carousel', AvReferenceCarousel);
if (!customElements.get('av-reference-product')) customElements.define('av-reference-product', AvReferenceProduct);
